import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const LANAP = () => {
  // FAQ Schema for Voice Search - LANAP Questions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is LANAP laser gum treatment?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LANAP (Laser Assisted New Attachment Procedure) is an FDA-cleared laser treatment for gum disease that uses a specialized dental laser to remove diseased tissue while preserving healthy gums. Unlike traditional gum surgery, LANAP requires no cutting or sutures, resulting in faster healing and less discomfort."
        }
      },
      {
        "@type": "Question",
        "name": "How much does LANAP cost in Fort Worth?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LANAP treatment costs vary based on the severity of gum disease and number of areas treated. Fort Worth Dental offers FREE LANAP consultations to provide accurate pricing. Many dental insurance plans cover LANAP, and we offer flexible financing options."
        }
      },
      {
        "@type": "Question",
        "name": "Is LANAP painful?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LANAP is significantly less painful than traditional gum surgery. Most patients report minimal discomfort during and after the procedure. Local anesthesia is used, and because there's no cutting or sutures, recovery is much faster and more comfortable."
        }
      },
      {
        "@type": "Question",
        "name": "How long does LANAP recovery take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most LANAP patients can return to normal activities within 24 hours. Full healing typically takes 2-4 weeks, compared to several months for traditional gum surgery. You can usually eat soft foods the same day as treatment."
        }
      },
      {
        "@type": "Question",
        "name": "Am I a candidate for LANAP?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LANAP is ideal for patients with moderate to severe gum disease (periodontitis) who want to avoid traditional surgery. Good candidates have gum pockets of 4mm or deeper. Schedule a free consultation at Fort Worth Dental to determine if LANAP is right for you."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>LANAP Laser Gum Treatment Fort Worth TX | Gum Disease Treatment | Fort Worth Dental</title>
        <meta name="description" content="LANAP laser gum disease treatment in Fort Worth TX. FDA-cleared, minimally invasive periodontal treatment. No scalpels, no sutures. FREE consultation. Call (817) 985-3337." />
        <meta name="keywords" content="LANAP fort worth, laser gum treatment, gum disease treatment fort worth tx, periodontal treatment, laser periodontal surgery, gum disease laser therapy, lanap cost, lanap near me" />
        <link rel="canonical" href="https://fortworthdental.com/lanap" />
        <meta property="og:title" content="LANAP Laser Gum Treatment Fort Worth TX" />
        <meta property="og:description" content="FDA-cleared LANAP laser gum disease treatment. Minimally invasive, faster recovery." />
        <meta property="og:url" content="https://fortworthdental.com/lanap" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examining.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">LANAP® Laser Gum Disease Treatment in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Advanced Laser Treatment for Gum Disease Without Traditional Surgery</p>
            <p className="text-sm mt-4 opacity-90">FDA-Cleared LANAP® | Minimally Invasive | No Scalpels or Sutures</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What is LANAP */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/lanap-new-1.jpg" 
                  alt="LANAP laser gum treatment procedure at Fort Worth Dental Texas" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What is LANAP®?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  LANAP® (Laser Assisted New Attachment Procedure) is an FDA-cleared laser treatment for gum disease. This minimally invasive procedure uses the PerioLase® MVP-7™ laser to remove diseased tissue and bacteria while preserving healthy gums.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Unlike traditional gum surgery that uses scalpels and sutures, LANAP® offers a less painful alternative with faster healing times. The laser specifically targets diseased tissue while leaving healthy tissue intact, promoting natural regeneration of gum and bone tissue.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose LANAP */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Why Choose LANAP® Over Traditional Gum Surgery?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>Less Painful:</strong> No scalpels or sutures means less discomfort</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>Faster Recovery:</strong> Most patients return to normal activities immediately</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>Less Bleeding:</strong> Laser technology minimizes bleeding during and after the procedure</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>Preserves Healthy Tissue:</strong> Laser is highly selective, targeting only diseased areas</span>
                </li>
              </ul>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>Regenerates Bone & Tissue:</strong> Promotes natural healing and regrowth</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>No Gum Recession:</strong> Traditional surgery can cause visible gum line changes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>Little to No Downtime:</strong> Resume normal activities the same day</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-3 text-xl">✓</span>
                  <span><strong>FDA Cleared:</strong> Proven safe and effective treatment</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Are You a Good Candidate */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Are You a Good Candidate for LANAP®?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  LANAP® is an excellent option for patients diagnosed with moderate to severe gum disease (periodontitis). You may be a good candidate if you:
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">•</span>
                    <span>Have bleeding, swollen, or receding gums</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">•</span>
                    <span>Have been diagnosed with periodontal disease</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">•</span>
                    <span>Want to avoid traditional gum surgery</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">•</span>
                    <span>Have loose teeth due to bone loss</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/lanap-new-2.jpg" 
                  alt="LANAP gum disease treatment candidate consultation Fort Worth" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Our LANAP Certified Doctor */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Our LANAP® Certified Doctor
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-8">
              At Fort Worth Dental, Dr. Joshua Pham is our LANAP-certified specialist. He earned his Doctor of Dental Surgery from the Baylor College of Dentistry in Dallas in 2011. LANAP (Laser-Assisted New Attachment Procedure) is an FDA-cleared, minimally invasive laser treatment for moderate-to-severe gum disease. It promotes regeneration of bone and tissue, offering less pain, faster healing and minimal gum recession.
            </p>
            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-lg text-center max-w-sm shadow-lg">
                <div className="w-40 h-40 mx-auto mb-4 rounded-full overflow-hidden border-4 border-teal-200">
                  <img src="/images/team/dr-pham.jpg" alt="Dr. Joshua Pham LANAP certified dentist Fort Worth" className="w-full h-full object-cover object-top" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Dr. Joshua Pham</h3>
                <p className="text-teal-600 font-semibold text-lg">D.D.S.</p>
                <p className="text-gray-600 mt-2">LANAP® Certified Specialist</p>
                <p className="text-gray-500 mt-3 text-sm">
                  Dr. Pham combines his passion for patient care with advanced laser technology to provide gentle, effective gum disease treatment with faster healing times.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your FREE LANAP® Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn if LANAP® laser gum treatment is right for you</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your LANAP® Consultation in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default LANAP;
